import { useTranslation } from "react-i18next";
import { ArrowLeft, DoorOpen, MousePointerClick } from "lucide-react";
import { Button } from "components/common/Button";
import { ConfirmActionModal } from "../ConfirmActionModal";
import { PendingRoomRow } from "./PendingRoomRow";
import type { RoomsCreationModeProps } from "./types";

export const RoomsCreationMode = ({ state }: RoomsCreationModeProps) => {
  const { t } = useTranslation();
  const {
    pendingRooms,
    isDrawing,
    isSaving,
    selectedTarget,
    draftModal,
    isDiscardModalOpen,
    toggleDrawing,
    selectPendingRoom,
    renamePendingRoom,
    removePendingRoom,
    saveAll,
    requestLeave,
    confirmLeave,
    closeDiscardModal,
    confirmDiscardDraft,
    closeDraftModal,
  } = state;

  const hasPendingRooms = pendingRooms.length > 0;
  const hasBlankName = pendingRooms.some((room) => room.name.trim() === "");

  return (
    <div className="w-full h-full bg-white rounded-lg border border-gray-200 shadow-sm p-4 overflow-hidden flex flex-col gap-3">
      <div className="w-full">
        <div className="flex items-center gap-2">
          <Button
            variant="default"
            size="sm"
            onClick={requestLeave}
            startAdornment={<ArrowLeft className="w-4 h-4" />}
            aria-label={t("common.back")}
          />
          <h2 className="text-sm font-semibold text-gray-900">
            {t("smartSurveyor.rooms.drawTitle")}
          </h2>
        </div>
        <div className="mt-3 w-full flex items-center gap-2">
          <Button
            variant={isDrawing ? "primary" : "outline"}
            size="sm"
            onClick={toggleDrawing}
            startAdornment={<MousePointerClick className="w-3.5 h-3.5" />}
          >
            {isDrawing
              ? t("smartSurveyor.rooms.finishRoom")
              : t("smartSurveyor.rooms.drawRoom")}
          </Button>
        </div>
      </div>
      {!hasPendingRooms && (
        <div className="flex-1 flex flex-col items-center justify-center gap-2 text-gray-400">
          <DoorOpen className="w-10 h-10 text-blue-500" />
          <p className="text-sm text-gray-500 text-center px-3">
            {isDrawing
              ? t("smartSurveyor.rooms.drawingHint")
              : t("smartSurveyor.rooms.drawingPausedHint")}
          </p>
        </div>
      )}
      {hasPendingRooms && (
        <>
          <div className="flex-1 overflow-y-auto flex flex-col gap-2 p-px">
            {pendingRooms.map((room) => (
              <PendingRoomRow
                key={room.id}
                room={room}
                isSelected={
                  selectedTarget?.type === "saved" &&
                  selectedTarget.id === room.id
                }
                onSelect={selectPendingRoom}
                onRename={renamePendingRoom}
                onRemove={removePendingRoom}
              />
            ))}
          </div>
          <div className="flex gap-2">
            <Button className="flex-1" onClick={requestLeave}>
              {t("smartSurveyor.rooms.cancelAll")}
            </Button>
            <Button
              className="flex-1"
              variant="primary"
              onClick={saveAll}
              // A room still being drawn would be lost; finish it first.
              disabled={isSaving || isDrawing || hasBlankName}
            >
              {isSaving
                ? t("smartSurveyor.rooms.saving")
                : t("smartSurveyor.rooms.saveAll")}
            </Button>
          </div>
        </>
      )}
      <ConfirmActionModal
        isOpen={draftModal?.type === "discardDraft"}
        onClose={closeDraftModal}
        onConfirm={confirmDiscardDraft}
        action="delete"
        title={t("smartSurveyor.rooms.discardModal.title")}
        description={t("smartSurveyor.rooms.discardModal.description")}
      />
      <ConfirmActionModal
        isOpen={isDiscardModalOpen}
        onClose={closeDiscardModal}
        onConfirm={confirmLeave}
        action="delete"
        title={t("smartSurveyor.rooms.discardAllModal.title")}
        description={t("smartSurveyor.rooms.discardAllModal.description")}
      />
    </div>
  );
};
